import React, { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Link2, Loader2, Play, Check, X, ChevronDown, ChevronUp,
  Zap, BarChart3, Undo2, Eye, CheckCheck, AlertTriangle, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  pending: 'bg-amber-100 text-amber-700',
  done: 'bg-green-100 text-green-700',
  no_links: 'bg-gray-100 text-gray-500',
};

const BulkLinkInsertion = ({ onClose }) => {
  const [step, setStep] = useState('idle');
  const [scanData, setScanData] = useState(null);
  const [selectedBlogs, setSelectedBlogs] = useState(new Set());
  const [previewData, setPreviewData] = useState({});
  const [expandedBlog, setExpandedBlog] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [beforeMetrics, setBeforeMetrics] = useState(null);
  const [afterMetrics, setAfterMetrics] = useState(null);

  const fetchMetrics = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/internal-links/insert/metrics`);
      return r.ok ? await r.json() : null;
    } catch { return null; }
  };

  const scan = useCallback(async () => {
    setStep('scanning');
    const bm = await fetchMetrics();
    setBeforeMetrics(bm);
    try {
      const res = await fetch(`${BACKEND_URL}/api/internal-links/insert/bulk/scan`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setScanData(data);
        setSelectedBlogs(new Set(data.pending.map(b => b.blog_id)));
        setStep('scanned');
      }
    } catch { toast.error('Scan failed'); setStep('idle'); }
  }, []);

  const previewBlog = async (blogId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/internal-links/insert/${blogId}/preview`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setPreviewData(prev => ({ ...prev, [blogId]: data }));
      }
    } catch (err) { console.error('Preview fetch failed:', err); }
  };

  const applyAll = async () => {
    setStep('applying');
    const ids = [...selectedBlogs];
    try {
      const res = await fetch(`${BACKEND_URL}/api/internal-links/insert/bulk/apply`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ blog_ids: ids }),
      });
      if (res.ok) {
        const data = await res.json();
        setApplyResult(data);
        const am = await fetchMetrics();
        setAfterMetrics(am);
        setStep('done');
        toast.success(`Inserted ${data.total_inserted} links into ${data.blogs_processed} blogs!`);
      }
    } catch { toast.error('Apply failed'); setStep('scanned'); }
  };

  const rollbackAll = async () => {
    if (!applyResult?.batch_id) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/internal-links/insert/bulk/rollback`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ batch_id: applyResult.batch_id }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Rolled back ${data.rolled_back} blogs`);
        const am = await fetchMetrics();
        setAfterMetrics(am);
      }
    } catch { toast.error('Rollback failed'); }
  };

  const toggleBlog = (id) => {
    setSelectedBlogs(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const isLoading = ['scanning', 'applying'].includes(step);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-6 overflow-y-auto" data-testid="bulk-insertion-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl mb-10">
        <div className="p-6 border-b flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Link2 className="w-5 h-5 text-blue-600" /> Bulk Link Insertion into HTML
            </h2>
            <p className="text-sm text-gray-500 mt-1">Insert approved internal links into live blog content</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="close-insertion-modal"><X className="w-4 h-4" /></Button>
        </div>

        <div className="p-6 max-h-[75vh] overflow-y-auto">
          {/* Idle */}
          {step === 'idle' && (
            <div className="text-center py-12">
              <Zap className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Insert Approved Links into Blog HTML</h3>
              <p className="text-sm text-gray-500 mb-6 max-w-lg mx-auto">
                Scans all published blogs for approved internal links not yet embedded in HTML content.
                Creates a snapshot before insertion for safe rollback.
              </p>
              <Button onClick={scan} className="bg-blue-600 hover:bg-blue-700" data-testid="start-insertion-scan">
                <Play className="w-4 h-4 mr-2" /> Scan for Pending Insertions
              </Button>
            </div>
          )}

          {/* Loading */}
          {isLoading && (
            <div className="text-center py-12">
              <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
              <p className="text-lg font-semibold text-gray-700">
                {step === 'scanning' ? 'Scanning blogs...' : 'Inserting links into content...'}
              </p>
            </div>
          )}

          {/* Scanned */}
          {step === 'scanned' && scanData && (
            <div className="space-y-6">
              <div className="grid grid-cols-3 gap-4">
                {[
                  { label: 'Pending Insertion', value: scanData.pending_count, color: 'amber' },
                  { label: 'Already Inserted', value: scanData.done_count, color: 'green' },
                  { label: 'Total Pending Links', value: scanData.total_pending_links, color: 'blue' },
                ].map((s, i) => (
                  <Card key={r.blog_slug || `result-${i}`}>
                    <CardContent className="p-4 text-center">
                      <p className={`text-3xl font-bold text-${s.color}-600`} data-testid={`iscan-${i}`}>{s.value}</p>
                      <p className="text-xs text-gray-500 mt-1">{s.label}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {scanData.pending.length === 0 ? (
                <div className="text-center py-8">
                  <Check className="w-12 h-12 text-green-500 mx-auto mb-3" />
                  <p className="text-gray-600">All approved links are already inserted. Nothing to do!</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-sm">Blogs with pending insertions ({selectedBlogs.size} selected)</h3>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => setSelectedBlogs(new Set(scanData.pending.map(b => b.blog_id)))}>Select All</Button>
                      <Button size="sm" variant="outline" onClick={() => setSelectedBlogs(new Set())}>Clear</Button>
                    </div>
                  </div>

                  <div className="space-y-1.5 max-h-72 overflow-y-auto border rounded-lg p-2">
                    {scanData.pending.map(b => {
                      const isOpen = expandedBlog === b.blog_id;
                      const pv = previewData[b.blog_id];
                      return (
                        <div key={b.blog_id} className="border rounded-lg" data-testid={`iblog-${b.blog_id}`}>
                          <div className="flex items-center gap-3 p-2 hover:bg-gray-50 cursor-pointer">
                            <input type="checkbox" checked={selectedBlogs.has(b.blog_id)}
                              onChange={() => toggleBlog(b.blog_id)}
                              className="w-4 h-4 rounded border-gray-300 text-blue-600" />
                            <Badge className={STATUS_BADGE[b.status]}>{b.pending_count} pending</Badge>
                            <span className="text-sm flex-1 truncate">{b.title}</span>
                            <span className="text-[10px] text-gray-400">{b.inserted_count} already</span>
                            <Button size="sm" variant="ghost" className="h-6 text-xs text-blue-600"
                              onClick={(e) => { e.stopPropagation(); previewBlog(b.blog_id); setExpandedBlog(isOpen ? null : b.blog_id); }}
                              data-testid={`preview-insert-${b.blog_id}`}>
                              <Eye className="w-3 h-3 mr-1" /> Preview
                            </Button>
                          </div>
                          {isOpen && pv && (
                            <div className="px-4 pb-3 border-t bg-gray-50">
                              <div className="grid grid-cols-2 gap-3 mt-2 text-xs">
                                <div>
                                  <p className="font-semibold text-green-600 mb-1">Would Insert ({pv.preview_inserted?.length || 0})</p>
                                  {(pv.preview_inserted || []).map((lk, i) => (
                                    <p key={lk.anchor_text || `inserted-${i}`} className="text-gray-600 py-0.5">"{lk.anchor_text}" → {lk.target_url}</p>
                                  ))}
                                </div>
                                <div>
                                  <p className="font-semibold text-gray-400 mb-1">Would Skip ({pv.preview_skipped?.length || 0})</p>
                                  {(pv.preview_skipped || []).map((lk, i) => (
                                    <p key={lk.anchor_text || `skipped-${i}`} className="text-gray-400 py-0.5">"{lk.anchor_text}" — {lk.reason}</p>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <Button onClick={applyAll} disabled={selectedBlogs.size === 0}
                    className="w-full bg-blue-600 hover:bg-blue-700 py-5" data-testid="apply-insertions-btn">
                    <CheckCheck className="w-4 h-4 mr-2" /> Insert Links into {selectedBlogs.size} Blogs
                  </Button>
                </>
              )}
            </div>
          )}

          {/* Done */}
          {step === 'done' && (
            <div className="space-y-6">
              <div className="text-center py-6">
                <Check className="w-16 h-16 text-blue-600 mx-auto mb-3" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Links Inserted!</h3>
                {applyResult && (
                  <p className="text-gray-600">
                    <strong>{applyResult.total_inserted}</strong> links inserted into <strong>{applyResult.blogs_processed}</strong> blogs
                    {applyResult.total_skipped > 0 && <> | <span className="text-gray-400">{applyResult.total_skipped} skipped</span></>}
                    {applyResult.failed > 0 && <> | <span className="text-red-500">{applyResult.failed} failed</span></>}
                  </p>
                )}
              </div>

              {/* Per-blog results */}
              {applyResult?.results && (
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {applyResult.results.map((r, i) => (
                    <div key={l.target_url || `link-${i}`} className="flex items-center justify-between text-xs p-2 bg-gray-50 rounded">
                      <span className="truncate max-w-md">{r.slug}</span>
                      <div className="flex gap-2">
                        <Badge className="bg-green-100 text-green-700">{r.inserted} inserted</Badge>
                        {r.skipped > 0 && <Badge className="bg-gray-100 text-gray-500">{r.skipped} skipped</Badge>}
                        {r.status === 'failed' && <Badge className="bg-red-100 text-red-700">failed</Badge>}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Before/After */}
              {beforeMetrics && afterMetrics && (
                <Card data-testid="insertion-before-after">
                  <CardHeader><CardTitle className="text-sm flex items-center gap-2"><BarChart3 className="w-4 h-4 text-blue-600" /> Before vs After</CardTitle></CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">Before</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between"><span>Blogs with Live Links</span><span className="font-bold">{beforeMetrics.blogs_with_live_links}</span></div>
                          <div className="flex justify-between"><span>Links Inserted</span><span className="font-bold">{beforeMetrics.total_inserted}</span></div>
                          <div className="flex justify-between"><span>Pending Insertion</span><span className="font-bold text-amber-600">{beforeMetrics.pending_insertion}</span></div>
                          <div className="flex justify-between"><span>Success Rate</span><span className="font-bold">{beforeMetrics.insertion_success_rate}%</span></div>
                        </div>
                      </div>
                      <div>
                        <h4 className="text-xs font-semibold text-blue-600 uppercase mb-3">After</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between"><span>Blogs with Live Links</span><span className="font-bold text-blue-600">{afterMetrics.blogs_with_live_links}</span></div>
                          <div className="flex justify-between"><span>Links Inserted</span><span className="font-bold text-blue-600">{afterMetrics.total_inserted}
                            <span className="text-xs ml-1">+{afterMetrics.total_inserted - beforeMetrics.total_inserted}</span></span></div>
                          <div className="flex justify-between"><span>Pending Insertion</span><span className="font-bold text-green-600">{afterMetrics.pending_insertion}</span></div>
                          <div className="flex justify-between"><span>Success Rate</span><span className="font-bold text-blue-600">{afterMetrics.insertion_success_rate}%</span></div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              <div className="flex gap-3">
                <Button onClick={rollbackAll} variant="outline" className="flex-1 border-red-300 text-red-600 hover:bg-red-50" data-testid="rollback-all-btn">
                  <Undo2 className="w-4 h-4 mr-2" /> Rollback Batch
                </Button>
                <Button onClick={() => { setStep('idle'); setScanData(null); setApplyResult(null); }}
                  variant="outline" className="flex-1">
                  <RefreshCw className="w-4 h-4 mr-2" /> Run Again
                </Button>
                <Button onClick={onClose} className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="insertion-done-btn">Done</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkLinkInsertion;
